import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB} from 'utils/constants';
import {ANALYTIC_EVENTS, Analytics} from '../../../analytics';
import {getFileSizeMB} from 'utils/common';
import {compressImage} from 'utils/compressImage';
import notIncludedIcon from 'assets/icons/not-included-icon.svg?no-inline';
import checkIcon from 'assets/icons/check.svg?no-inline';
import {IncludedRow, StyledUploadPhoto, Photo} from './styled';

function getAllowedFormats() {
  return {
    regExpFormats: '.jpg|.jpeg|.png',
    errorMessage: 'incorrect_file_type_only_file_types_allowed',
  };
}

type UploadPhotoProps = {
  image: string | null;
  onUpload: (screenshot: string) => void;
  onDisabled: (value: boolean) => void;
};
const UploadPhoto = React.memo(({image, onUpload, onDisabled}: UploadPhotoProps) => {
  const {t} = useTranslation();
  const [validationError, setValidationError] = useState<string | null>();
  const ref = React.useRef<HTMLInputElement>(null);

  const handleImage = (image?: File) => {
    if (!image) return;
    compressImage(image)
      .then(onUpload)
      .catch(error => {
        const errorMessage = error?.message || 'Failed to process image';
        setValidationError(errorMessage);
        Analytics.track(ANALYTIC_EVENTS.ivUploadingPhotoError, {
          'Error message': errorMessage,
        });
        onDisabled(true);
      });
  };

  const validationImage = (image?: File) => {
    const fileSize = getFileSizeMB(image);

    if (!image) return false;

    const allowedFormats = getAllowedFormats();
    const regExpAllowedFormats = new RegExp(`(${allowedFormats.regExpFormats})`, 'i');
    const imageName = image?.name || '';

    if (!regExpAllowedFormats.test(imageName)) {
      const errorMessage = t(allowedFormats.errorMessage);
      setValidationError(errorMessage);
      Analytics.track(ANALYTIC_EVENTS.ivUploadingPhotoError, {
        'Error message': errorMessage,
      });
      onDisabled(true);
      return false;
    }

    if (!fileSize || fileSize > IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB) {
      const errorMessage = t('max_file_size_is_number_mb', {
        number: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
      });
      setValidationError(errorMessage);
      Analytics.track(ANALYTIC_EVENTS.ivUploadingPhotoError, {
        'Error message': errorMessage,
      });
      onDisabled(true);
      return false;
    }

    setValidationError(null);
    onDisabled(false);
    return true;
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const image = e?.currentTarget?.files?.[0];
    const isSuccessfullyValidated = validationImage(image);
    if (isSuccessfullyValidated) handleImage(image);
  };

  return (
    <>
      {image ? (
        <Photo
          src={image}
          alt="Preloaded"
          className="photo-box__uploaded-photo upload-box__row"
        />
      ) : (
        <StyledUploadPhoto
          ref={ref}
          error={validationError}
          onChange={handleUpload}
          helperText={t<string>('accepted_file_formats_and_max_size', {
            number: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
          })}
        />
      )}
      <IncludedRow>
        <img src={notIncludedIcon} alt="Not included" width={24} height={24} />
        <p>{t('there_should_not_be_reflections')}</p>
      </IncludedRow>
      <IncludedRow>
        <img src={checkIcon} alt="Included" width={24} height={24} />
        <p>{t('use_clear_not_blurred_image')}</p>
      </IncludedRow>
    </>
  );
});

export {UploadPhoto};
